<template>

  <div class="d-flex flex-column align-center justify-center client-geo-location">
    <!-- Message Area -->
    <div class="d-flex align-center client-coords-text-main">
      <v-icon color="amber darken-2" style="font-size:22px;" class="pr-2">mdi-alert-box-outline</v-icon>
      <div>{{ mainMessage }}</div>
    </div>
    <div class="mt-2 px-3 d-flex align-center justify-center justify-sm-start" v-if="showSubMessage">
      <div class="client-coords-text-sub" v-if="enableTrueReRoute">{{ subMessageTrueReroute }}</div>
      <div class="client-coords-text-sub" v-else>{{ subMessage }}</div>
    </div>

    <!-- Loading -->
    <template v-if="isLoading">
      <v-progress-circular indeterminate :size="24" color="primary" class="mt-3" />
    </template>

    <!-- Loading Error (show on Prod only) -->
    <template v-else-if="loadingError && !isDev">
      <div class="mt-3 d-flex align-center">
        <div class="mr-3 loading-error-text">{{ loadingError }}</div>
        <v-btn small color="grey darken-3" @click="cancel">Ok</v-btn>
      </div>
    </template>

    <!-- Dev Display (only if NODE_NEV === development) -->
    <!-- Allows manual entry for deving purposes -->
    <template v-else-if="isDev">
      <div class="mt-5 d-flex align-center">
        <div class="mr-4">Coords:</div>
        <v-text-field
            outlined
            :readonly="!isDev"
            hide-details
            label="Latitude"
            style="max-width:130px;"
            v-model="activeClientLatitude" />
        <v-text-field
            outlined
            :readonly="!isDev"
            hide-details
            label="Longitude"
            style="max-width:130px;"
            class="ml-5"
            v-model="activeClientLongitude" />
        <div class="ml-3 client-coords-accuracy-text">±{{ activeClientLocationAccuracy }} m</div>
      </div>
    </template>

    <!-- Prod Display -->
    <!-- Shows success message with coords in small text -->
    <template v-else>
      <div class="mt-3 d-flex align-center">
        <v-icon style="font-size:20px;" class="mr-2" color="green">mdi-check-circle</v-icon>
        <div class="coords-found-text">Location found</div>
      </div>
      <div class="mt-1 d-flex align-center">
        <div class="d-flex align-center coords-display-text">
          <div>{{ activeClientLatitude }}</div>
          <div class="pr-1">,</div>
          <div>{{ activeClientLongitude }}</div>
          <div class="ml-2" style="font-size:12px;">±{{ activeClientLocationAccuracy }} m</div>
        </div>
      </div>
    </template>
  </div>

</template>

<script>

export default {
  name: 'ClientGeoLocation',

  props: {
    mainMessage: {
      type: String,
      default: 'Your route is changing'
    },
    showSubMessage: {
      type: Boolean,
      default: true
    },
    subMessage: {
      type: String,
      default: 'Your current location will be marked on the map.'
    },
    subMessageTrueReroute: {
      type: String,
      default: 'Your current location will be used in the new route.'
    },
    enableTrueReRoute: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isLoading: false,
      loadingError: null,
      activeClientLatitude: null,
      activeClientLongitude: null,
      activeClientLocationAccuracy: null,
      maxRetries: 3
    }
  },

  computed: {
    isDev () {
      return process.env.NODE_ENV === 'development'
    }
  },

  beforeMount () {
    this.loadClientCoordindates()
  },

  methods: {
    // Returns the loaded geolocation info
    getInfo () {
      return {
        latitude: this.activeClientLatitude,
        longitude: this.activeClientLongitude,
        accuracy: this.activeClientLocationAccuracy
      }
    },

    // Emits a cancel action
    cancel () {
      this.loadingError = null
      this.$emit('canceled')
    },

    // Attempt to access geolocation of client
    loadClientCoordindates (retryCount = 0) {
      // console.log('[DEVING] loading client geolocation')
      if (!('geolocation' in navigator)) {
        console.log('Geolocation not supported')
        return
      }

      const options = {
        enableHighAccuracy: true, // try GPS/Wi-Fi first
        timeout: 10_000, // give up after 10 s
        maximumAge: 0 // don't use cached fix
      }

      const getPosition = () => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude, accuracy } = pos.coords
            console.log(`Lat: ${latitude}, Lng: ${longitude}, ±${accuracy} m`)
            this.activeClientLatitude = latitude
            this.activeClientLongitude = longitude
            this.activeClientLocationAccuracy = accuracy
            this.isLoading = false
          },
          (err) => {
            console.error('Geolocation error =>', err.message)
            // If we get POSITION_UNAVAILABLE and haven't exceeded max retries, try again
            if (err.code === 2 && retryCount < this.maxRetries) {
              console.log(`Position unavailable, retrying in 2 seconds... (attempt ${retryCount + 1}/${this.maxRetries})`)
              setTimeout(() => this.loadClientCoordindates(retryCount + 1), 2000)
            } else if (retryCount >= this.maxRetries) {
              console.log('Max retries reached. Giving up on getting client location')
              this.loadingError = 'Cannot find your location. Try again in a few seconds.'
              this.isLoading = false
            }
          },
          options
        )
      }

      // Load coordinates
      this.isLoading = true
      getPosition()
    }
  }
}
</script>

<style scoped>
  .client-coords-text-main {
    font-size: 1.08rem;
  }

  .client-coords-text-sub {
    color: #9c9c9c;
    font-size: 0.93rem;
    font-style: italic;
  }

  .loading-error-text {
    color: #ff5252;
    font-size: 0.9rem;
  }

  .coords-found-text {
    color: #4caf4f;
    font-size: 1.0rem;
  }

  .coords-display-text {
    color: #525252;
    font-size: 0.9rem;
  }

  .client-coords-accuracy-text {
    color: #727272;
    font-size: 0.9rem;
  }
</style>
